import { useMemo, useState } from 'react'
import type {
  BulkCreateResponse,
  CaseEndpoint,
  EndpointRelationship,
} from '../../../types/case-container'

interface UseCopyLinksFromModalArgs {
  targetEndpoint: CaseEndpoint
  endpoints: CaseEndpoint[]
  relationships: EndpointRelationship[]
  onCopy: (fromEndpointId: string) => Promise<BulkCreateResponse | null>
  onClose: () => void
}

export interface CopyLinksFromModalState {
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  selectedEndpointId: string | null
  setSelectedEndpointId: React.Dispatch<React.SetStateAction<string | null>>
  copySuccess: { count: number } | null
  endpointLinkCounts: Map<string, number>
  filteredEndpoints: CaseEndpoint[]
  selectedLinkCount: number
  handleCopy: () => Promise<void>
  handleClose: () => void
}

function buildLinkCounts(relationships: EndpointRelationship[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const rel of relationships) {
    const current = counts.get(rel.sourceEndpointId) ?? 0
    counts.set(rel.sourceEndpointId, current + 1)
  }
  return counts
}

interface FilterArgs {
  endpoints: CaseEndpoint[]
  targetId: string
  searchTerm: string
  endpointLinkCounts: Map<string, number>
}

function filterAndSortEndpoints(args: FilterArgs): CaseEndpoint[] {
  const { endpoints, targetId, searchTerm, endpointLinkCounts } = args
  const search = searchTerm.toLowerCase()
  return endpoints
    .filter((ep) => {
      if (ep.id === targetId) return false
      if ((endpointLinkCounts.get(ep.id) ?? 0) === 0) return false
      if (!searchTerm) return true
      return (
        ep.hostname.toLowerCase().includes(search) ||
        ep.name.toLowerCase().includes(search) ||
        (ep.ipAddresses?.some((ip) => ip.includes(search)) ?? false)
      )
    })
    .sort((left, right) => {
      const countA = endpointLinkCounts.get(left.id) ?? 0
      const countB = endpointLinkCounts.get(right.id) ?? 0
      if (countB !== countA) return countB - countA
      return left.hostname.localeCompare(right.hostname)
    })
}

export function useCopyLinksFromModal(args: UseCopyLinksFromModalArgs): CopyLinksFromModalState {
  const { targetEndpoint, endpoints, relationships, onCopy, onClose } = args
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null)
  const [copySuccess, setCopySuccess] = useState<{ count: number } | null>(null)

  const endpointLinkCounts = useMemo(() => buildLinkCounts(relationships), [relationships])
  const filteredEndpoints = useMemo(
    () =>
      filterAndSortEndpoints({
        endpoints,
        targetId: targetEndpoint.id,
        searchTerm,
        endpointLinkCounts,
      }),
    [endpoints, targetEndpoint.id, searchTerm, endpointLinkCounts]
  )
  const selectedLinkCount = selectedEndpointId
    ? (endpointLinkCounts.get(selectedEndpointId) ?? 0)
    : 0

  const handleCopy = async (): Promise<void> => {
    if (!selectedEndpointId) return
    const result = await onCopy(selectedEndpointId)
    if (result) setCopySuccess({ count: result.createdCount })
  }

  const handleClose = (): void => {
    setCopySuccess(null)
    setSelectedEndpointId(null)
    onClose()
  }

  return {
    searchTerm,
    setSearchTerm,
    selectedEndpointId,
    setSelectedEndpointId,
    copySuccess,
    endpointLinkCounts,
    filteredEndpoints,
    selectedLinkCount,
    handleCopy,
    handleClose,
  }
}
